import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitListing } from '../../actions/business';

const EMPTY = {
  name: '',
  town_id: '',
  category_id: '',
  tagline: '',
  description: '',
  phone: '',
  email: '',
  website: '',
  instagram: '',
  address_line1: '',
  zip: '',
  season: 'unknown',
  company: '',
};

export default function SubmitListing() {
  const dispatch = useDispatch();
  const { towns, categories } = useSelector((state) => state.config);
  const submission = useSelector((state) => state.submission);
  const sending = useSelector((state) => Boolean(state.api.loading.SUBMIT));

  const [form, setForm] = useState(EMPTY);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();

    // Blanks are dropped rather than sent as "": the API's `nullable` rules pass
    // an empty string straight into the column, so a listing ends up with an
    // empty-string website that renders as a dead link.
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));

    dispatch(submitListing(payload));
  };

  if (submission.status === 'accepted') {
    return (
      <div className="wrap pad narrow">
        <h1>Got it</h1>
        <p>
          Thanks — the listing is in the queue. Everything is reviewed by a person before it goes
          live, so give it a day or two.
        </p>
      </div>
    );
  }

  const fieldError = (key) => submission.errors?.[key]?.[0];

  return (
    <div className="wrap pad narrow">
      <h1>Add a listing</h1>
      <p className="lede">Free to list. Reviewed by a person before it goes live.</p>

      {submission.status === 'failed' && !submission.errors && (
        <p className="alert">{submission.message}</p>
      )}

      <form onSubmit={onSubmit} className="form">
        <label>
          Business name
          <input value={form.name} onChange={set('name')} required maxLength={191} />
          {fieldError('name') && <span className="field-error">{fieldError('name')}</span>}
        </label>

        <label>
          Town
          <select value={form.town_id} onChange={set('town_id')} required>
            <option value="">Choose a town</option>
            {towns.map((town) => (
              <optgroup key={town.id} label={town.name}>
                <option value={town.id}>{town.name}</option>
                {town.communities?.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {fieldError('town_id') && <span className="field-error">{fieldError('town_id')}</span>}
        </label>

        <label>
          Category
          <select value={form.category_id} onChange={set('category_id')} required>
            <option value="">Choose a category</option>
            {categories.map((parent) => (
              <optgroup key={parent.id} label={parent.name}>
                {parent.children?.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {fieldError('category_id') && (
            <span className="field-error">{fieldError('category_id')}</span>
          )}
        </label>

        <label>
          One line about it
          <input value={form.tagline} onChange={set('tagline')} maxLength={191} />
        </label>

        <label>
          Description
          <textarea value={form.description} onChange={set('description')} rows={5} />
        </label>

        <div className="form-row">
          <label>
            Phone
            <input value={form.phone} onChange={set('phone')} />
          </label>
          <label>
            Email
            <input type="email" value={form.email} onChange={set('email')} />
          </label>
        </div>

        <div className="form-row">
          <label>
            Website
            <input
              type="url"
              placeholder="https://"
              value={form.website}
              onChange={set('website')}
            />
            {fieldError('website') && <span className="field-error">{fieldError('website')}</span>}
          </label>
          <label>
            Instagram
            <input value={form.instagram} onChange={set('instagram')} placeholder="@handle" />
          </label>
        </div>

        <div className="form-row">
          <label>
            Street address
            <input value={form.address_line1} onChange={set('address_line1')} />
          </label>
          <label>
            Zip
            <input value={form.zip} onChange={set('zip')} maxLength={10} />
          </label>
        </div>

        <label>
          Open when?
          <select value={form.season} onChange={set('season')}>
            <option value="unknown">Not sure yet</option>
            <option value="year_round">Year round</option>
            <option value="seasonal">Seasonal</option>
          </select>
        </label>

        {/* Honeypot. Hidden from people, filled in by scrapers; the API rejects
            any submission that carries it. Not `display:none` -- some bots skip
            those -- and aria-hidden plus tabIndex keep it away from screen
            readers and the tab order. */}
        <div className="hp" aria-hidden="true">
          <label>
            Company
            <input value={form.company} onChange={set('company')} tabIndex={-1} autoComplete="off" />
          </label>
        </div>

        <button type="submit" className="btn btn-primary" disabled={sending}>
          {sending ? 'Sending…' : 'Submit listing'}
        </button>
      </form>
    </div>
  );
}
